import { useQuery } from '@apollo/client';
import React, { useRef, useState } from 'react';
import ControlBarContributorsButton from './ControlBarContributorsButton';
import type { ControlBarInsightsSubmenu } from './ControlBarInsights';
import ControlBarInsights from './ControlBarInsights';
import * as styles from './ControlBarInsightsButton.module.scss';
import ControlBarPopOver from './ControlBarPopOver';
import LoadingIndicator from '../../components/LoadingIndicator';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import useControlledState from '../../utils/useControlledState';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';

const POST_FRAGMENT = gql(`
  fragment ControlBarInsightsButton_post on Post {
    id
    ...ControlBarContributorsButton_post
  }
`);

const POST_QUERY = gql(/** @complexity 21 */ `
  query ControlBarInsightsButtonPost($id: ID!) {
    post(id: $id) {
      id
      ...ControlBarInsights_post
    }
  }
`);

export type ControlBarInsightsState =
  | {
      isOpen: true;
      submenu: ControlBarInsightsSubmenu | null;
    }
  | {
      isOpen: false;
    };

interface ControlBarInsightsButtonProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  children: React.ReactNode;
  popOverPlacement: React.ComponentProps<typeof ControlBarPopOver>['placement'];
  state?: ControlBarInsightsState;
  onStateChange?: (state: ControlBarInsightsState) => void;
}

const ControlBarInsightsButton = ({
  post,
  children,
  popOverPlacement,
  state,
  onStateChange,
}: ControlBarInsightsButtonProps) => {
  const [controlBarInsightsState, setControlBarInsightsState] =
    useControlledState<ControlBarInsightsState>({
      defaultValue: {
        isOpen: false,
      },
      value: state,
      onValueChange: onStateChange,
    });
  const anchorRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [shouldLoadPopOverData, setShouldLoadPopOverData] = useState(false);

  const { data } = useQuery(POST_QUERY, {
    skip: !shouldLoadPopOverData && !controlBarInsightsState.isOpen,
    variables: { id: post.id },
  });

  if (isMobile) {
    return (
      <ControlBarContributorsButton post={post} interactionsDisabled>
        {children}
      </ControlBarContributorsButton>
    );
  }

  return (
    <ControlBarPopOver
      onOpenChange={(open) => {
        setControlBarInsightsState(
          open
            ? {
                isOpen: true,
                submenu: null,
              }
            : {
                isOpen: false,
              },
        );
      }}
      anchorRef={anchorRef}
      content={
        <div className={styles.contentContainer}>
          {data?.post ? (
            <ControlBarInsights
              post={data.post}
              submenu={
                controlBarInsightsState.isOpen
                  ? controlBarInsightsState.submenu
                  : null
              }
              setSubmenu={(submenu) => {
                setControlBarInsightsState({
                  isOpen: true,
                  submenu,
                });
              }}
              close={() =>
                setControlBarInsightsState({
                  isOpen: false,
                })
              }
            />
          ) : (
            <div className={styles.loading}>
              <LoadingIndicator />
            </div>
          )}
        </div>
      }
      placement={popOverPlacement}
      open={controlBarInsightsState.isOpen}
    >
      <ControlBarContributorsButton
        post={post}
        active={controlBarInsightsState.isOpen}
        avatarStackRef={anchorRef}
        onPointerDown={() => {
          setShouldLoadPopOverData(true);
        }}
      >
        {children}
      </ControlBarContributorsButton>
    </ControlBarPopOver>
  );
};

export default ControlBarInsightsButton;
